/* eslint-disable react/jsx-key */
import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Button, Form, Input, List, message } from 'antd-v5';
import { SettingsContent } from '../../../components/SettingsContent';
import {
  createAppointmentType,
  getAppointmentTypes,
  updateAppointmentType,
  deleteAppointmentType,
} from '../../../../../../services/appointments';

export const AppointmentTypes = () => {
  const [openFooter, setOpenFooter] = useState(false);
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [itemOpen, setItemOpen] = useState(null);

  const {
    data: appointmentTypes,
    isFetching,
    refetch,
  } = useQuery(
    'appointmentTypes',
    async () => {
      return getAppointmentTypes();
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const handleSuccess = useCallback(
    async data => {
      try {
        setConfirmLoading(true);
        const resp = await createAppointmentType(data);
        message.success(resp.unarchived ? `“${data.name}” has been restored as an appointment type` : 'Appointment type created successfully');
        refetch();
      } catch (error) {
        console.log(error);
        message.error('Error creating appointment type' + (error?.response?.data?.data ? `: ${error.response.data.data}` : ''));
      } finally {
        setItemOpen(null);
        setOpenFooter(false);
        form.resetFields();
        setConfirmLoading(false);
      }
    },
    [refetch],
  );

  const handleCancel = useCallback(() => {
    setOpenFooter(false);
    form.resetFields();
  }, [form]);

  return (
    <SettingsContent
      title="Appointments Types"
      description="Appointment types your clients can choose from. These appear on the appointment form."
    >
      <List
        className="w-100-P"
        size="small"
        dataSource={appointmentTypes}
        renderItem={item => (
          <AppointmentTypeItem
            item={item}
            refetch={refetch}
            open={itemOpen && itemOpen === item.id}
            setItemOpen={setItemOpen}
          />
        )}
        loading={isFetching}
        footer={
          openFooter && (
            <Form onFinish={handleSuccess} layout="inline" className="flex-row items-center gap-16" form={form}>
              <Form.Item noStyle name="name" rules={[{ required: true }]}>
                <Input placeholder="Appointment Type" style={{ maxWidth: '360px', width: '100%', marginRight: 0 }} />
              </Form.Item>
              <Button loading={confirmLoading} type="primary" htmlType="submit">
                Save
              </Button>
              <Button onClick={handleCancel} type="default" disabled={confirmLoading} htmlType="button">
                Cancel
              </Button>
            </Form>
          )
        }
      />
      <Button type="primary" className="m-top-16" onClick={() => setOpenFooter(true)}>
        Add Appointment Type
      </Button>
    </SettingsContent>
  );
};

const AppointmentTypeItem = ({ item, open, setItemOpen, refetch }) => {


  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    form.setFieldValue('name', item.name);
  }, [item.name]);

  const handleSuccess = useCallback(
    async data => {
      try {
        setConfirmLoading(true);
        await updateAppointmentType(item.id, data);
        message.success('Appointment type updated successfully');
      } catch (error) {
        console.error(error);
        message.error('Error updating appointment type');
      } finally {
        setItemOpen(null);
        refetch();
        setConfirmLoading(false);
      }
    },
    [item.id],
  );

  const handleDelete = useCallback(
    async () => {
      try {
        setConfirmLoading(true);
        await deleteAppointmentType(item.id);
        message.success('Appointment type archived successfully');
      } catch (error) {
        console.log(error);
        message.error('Error archiving appointment type');
      } finally {
        refetch();
        setConfirmLoading(false);
      }
    },
    [item.id, refetch],
  );

  const handleCancel = useCallback(() => {
    setItemOpen(null);
    form.setFieldValue('name', item.name);
  }, [form, setItemOpen, item.name]);

  return (
    <List.Item
      styles={{ actions: { margin: 0 } }}
      actions={
        !open
          ? [
            <Button onClick={() => setItemOpen(item.id)} variant="text" color="primary" htmlType="button">
              Edit
            </Button>,
          ]
          : [
            <Button
              onClick={() => handleSuccess(form.getFieldsValue())}
              variant="text"
              color="primary"
              htmlType="button"
              loading={confirmLoading}
            >
              Save
            </Button>,
            <Button onClick={handleDelete} variant="text" color="danger" htmlType="button" disabled={confirmLoading}>
              Archive
            </Button>,
            <Button onClick={handleCancel} variant="text" color="danger" htmlType="button" disabled={confirmLoading}>
              Cancel
            </Button>,
          ]
      }
      key={item.id}
    >
      <Form layout="inline" className="flex-row items-center gap-16" form={form} disabled={confirmLoading}>
        <Form.Item noStyle name="name" rules={[{ required: true }]}>
          {open ? (
            <Input
              placeholder="Appointment Type"
              onPressEnter={() => handleSuccess(form.getFieldsValue())}
              style={{ maxWidth: '360px', width: '100%', marginRight: 0 }}
            />
          ) : (
            item.name
          )}
        </Form.Item>
      </Form>
    </List.Item>
  );
};
